import React, { useCallback, useEffect, useRef } from 'react';
import { t } from '@lingui/macro';
import { FaPlay, FaStop } from 'react-icons/fa';

import { useAccessibilityContext } from '../Providers/AccessibilityProvider/AccessibilityContext';
import { AccessibilityOption } from './AccessibilityButton';

const StoryTellingOption: React.FC = () => {
  const { storytellerEnabled, setStorytellerEnabled, langTeller } = useAccessibilityContext();
  const synth = useRef<SpeechSynthesis | null>(null);

  useEffect(() => {
    synth.current = window.speechSynthesis;
  }, []);

  const speak = useCallback(
    (text: string) => {
      if (!synth.current) {
        console.error('Speech synthesis is not supported in this browser.');
        return;
      }

      // Cancel any ongoing speech
      synth.current.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      if (langTeller) {
        utterance.lang = langTeller;
      }

      synth.current.speak(utterance);
    },
    [langTeller]
  );

  const getTextContent = (element: Element): string => {
    if (element.nodeType === Node.TEXT_NODE) {
      return element.textContent || '';
    }

    if (element instanceof HTMLImageElement) {
      return element.alt || '';
    }

    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      return element.value || element.placeholder || '';
    }

    return Array.from(element.childNodes)
      .map((node) => getTextContent(node as Element))
      .join(' ')
      .trim();
  };

  const handleInteraction = useCallback(
    (e: Event) => {
      if (!storytellerEnabled) return;

      const target = e.target as HTMLElement;
      const text = getTextContent(target);
      if (text) {
        speak(text);
      }
    },
    [storytellerEnabled, speak]
  );

  const toggleStoryteller = () => {
    if (setStorytellerEnabled) {
      setStorytellerEnabled(!storytellerEnabled);
    }
  };

  useEffect(() => {
    if (storytellerEnabled) {
      document.body.addEventListener('mouseover', handleInteraction, true);
      document.body.addEventListener('focus', handleInteraction, true);
    } else {
      document.body.removeEventListener('mouseover', handleInteraction, true);
      document.body.removeEventListener('focus', handleInteraction, true);
      if (synth.current) {
        synth.current.cancel();
      }
    }

    return () => {
      document.body.removeEventListener('mouseover', handleInteraction, true);
      document.body.removeEventListener('focus', handleInteraction, true);
      if (synth.current) {
        synth.current.cancel();
      }
    };
  }, [storytellerEnabled, handleInteraction]);

  return (
    <AccessibilityOption
      name={'Narrador'}
      icon={
        storytellerEnabled ? (
          <FaStop className="h-3 w-3 fill-white" />
        ) : (
          <FaPlay className="h-3 w-3 fill-white" />
        )
      }
      onClick={toggleStoryteller}
    />
  );
};

export default StoryTellingOption;
